import { MetricProps } from './types';
import _last from 'lodash/last';
import _isNil from 'lodash/isNil';
import _pick from 'lodash/pick';
import _sortedIndexBy from 'lodash/sortedIndexBy';
import numeral from 'numeral';
import { getDayIdFromDate } from 'src/utils/dateformatting';
import ReduxStore from 'src/types/store/reduxStore';
import { warn } from 'src/utils/mixpanel';
import moment from 'moment';
import colors from 'src/utils/colors';
import { safeDivide } from 'src/utils/app';

export const computeDataSet = (
  metricsByDayId: Map<number, { actual?: number; projected?: number; budgetAmount: number }>
) => {
  const startDate = moment().startOf('month');
  const endDate = moment().endOf('month');

  const metrics: MetricProps = {
    dailyBudget: [],
    surplusBudget: [],
    overspend: [],
    padding: [],
    actualSpend: [],
    projectedSpend: [],
    totalCampaignBudgetSum: 0
  };

  let curDate = startDate.clone();
  let totalCampaignBudgetSum = 0;
  while (curDate.isBefore(endDate)) {
    const dayId = getDayIdFromDate(curDate.toDate());
    const timestamp = +curDate.format('x');

    // Get the spend for the week ID in question and then multiply by the daily multiplier
    let metricsForDay = metricsByDayId.get(dayId);
    if (!metricsForDay || (_isNil(metricsForDay.actual) && _isNil(metricsForDay.projected))) {
      warn(`Missing both daily spend metrics + projections for day id ${dayId}`);
      metricsForDay = { projected: 0, budgetAmount: 0 };
    }
    const { actual: actualSpend, projected: projectedSpend, budgetAmount: dailyBudget } = metricsForDay;
    metrics.dailyBudget.push([timestamp, dailyBudget]);
    totalCampaignBudgetSum += dailyBudget;
    const adSpendForDay = (metricsForDay.projected ? projectedSpend : actualSpend) || 0;
    if (metricsForDay.projected) {
      // Use projected spend metrics if we have no actual data and the dayId isn't in the past.
      metrics.projectedSpend.push([timestamp, adSpendForDay]);
      metrics.actualSpend.push([timestamp, 0]);
    } else {
      // Use actual spend metrics if we have them
      metrics.actualSpend.push([timestamp, adSpendForDay]);
      metrics.projectedSpend.push([timestamp, 0]);
    }

    const surplusBudget = dailyBudget - adSpendForDay;
    if (surplusBudget >= 0) {
      metrics.surplusBudget.push([timestamp, surplusBudget]);
      metrics.overspend.push([timestamp, 0]);
    } else {
      metrics.surplusBudget.push([timestamp, 0]);
      metrics.overspend.push([timestamp, -surplusBudget]);

      if (metricsForDay.projected) {
        _last(metrics.projectedSpend)![1] += surplusBudget;
      } else {
        _last(metrics.actualSpend)![1] += surplusBudget;
      }
    }

    curDate = curDate.add(1, 'day');
  }

  const maxValue = metrics.actualSpend.reduce(
    (acc, [_ts, actualSpend], i) =>
      Math.max(acc, actualSpend + metrics.projectedSpend[i][1] + metrics.overspend[i][1] + metrics.surplusBudget[i][1]),
    0
  );
  metrics.actualSpend.forEach((_, i) => {
    const timestamp = metrics.actualSpend[i][0];
    const padding =
      maxValue -
      (metrics.actualSpend[i][1] +
        metrics.projectedSpend[i][1] +
        metrics.overspend[i][1] +
        metrics.surplusBudget[i][1]);

    metrics.padding.push([timestamp, padding]);
  });

  metrics.totalCampaignBudgetSum = totalCampaignBudgetSum;

  return metrics;
};

export const buildChartSeries = (dataSet: ReturnType<typeof computeDataSet>) =>
  [
    { name: '', color: '#00000000', data: dataSet.padding },
    {
      name: 'Surplus Budget',
      color: colors.surplusBudget,
      data: dataSet.surplusBudget,
      borderRadiusTopLeft: '4px',
      borderRadiusTopRight: '4px'
    },
    {
      name: 'Overpacing',
      color: colors.overpacing,
      data: dataSet.overspend,
      borderRadiusTopLeft: '4px',
      borderRadiusTopRight: '4px'
    },
    { name: 'Actual Spend', color: colors.actualSpend, data: dataSet.actualSpend },
    {
      name: 'Projected Spend',
      color: colors.projectedSpend,
      data: dataSet.projectedSpend,
      borderRadiusTopLeft: '4px',
      borderRadiusTopRight: '4px'
    }
  ].map((series) => ({
    ...series,
    data: series.data.map(([timestamp, value]) => [timestamp, Math.round(value * 100) / 100])
  }));

const getSum = (arr: [number, number][]) => arr.reduce((a, b) => a + b[1], 0);

const projectedSpendColor = [
  { limit: 0.6, color: colors.errorRed },
  { limit: 0.8, color: colors.gold },
  { limit: 1.05, color: colors.brandClubGreen },
  { limt: Infinity, color: colors.errorRed }
];

export const formatLegendData = (
  dataset: {
    actualSpend: [number, number][];
    budget?: [number, number][];
    projectedSpend: [number, number][];
    overspend: [number, number][];
    padding: [number, number][];
  },
  totalMonthlyBudget: number
) => {
  const totalActualOverspend = getSum(dataset.overspend);
  const totalProjectedOverspend = getSum(dataset.overspend.filter((_, i) => dataset.projectedSpend[i][1] > 0));

  const actualSpend = getSum(dataset.actualSpend);
  const projectedSpend = getSum(dataset.projectedSpend);
  const totalSpend = actualSpend + totalActualOverspend + projectedSpend + totalProjectedOverspend;

  const actualSpendPercent = numeral(safeDivide(actualSpend + totalActualOverspend, totalMonthlyBudget, 0)).format('0.00%');
  const projectedSpendPercent = numeral(safeDivide(projectedSpend + totalProjectedOverspend, totalMonthlyBudget, 0)).format(
    '0.00%'
  );
  const totalSpendPercent = numeral(safeDivide(totalSpend, totalMonthlyBudget, 0)).format('0.00%');

  const rawTotalSpendPercent = safeDivide(totalSpend, totalMonthlyBudget, 0);
  // binary
  const colorIndex = _sortedIndexBy(
    projectedSpendColor,
    {
      limit: rawTotalSpendPercent,
      color: ''
    },
    'limit'
  );

  const color = projectedSpendColor[colorIndex]?.color;

  const mainLegendData = [
    {
      text: 'Actual Spend (MTD)',
      value: numeral(actualSpend + totalActualOverspend).format('$0.00a'),
      percentageValue: `${actualSpendPercent} of budget`
    },
    {
      text: 'Projected Additional Spend',
      value: numeral(projectedSpend + totalProjectedOverspend).format('$0.00a'),
      percentageValue: `${projectedSpendPercent} of budget`
    },
    {
      text: 'Projected Total Spend',
      value: numeral(totalSpend).format('$0.00a'),
      percentageValue: `${totalSpendPercent} of budget`,
      percentColor: color
    },
    { text: 'Total Budget', value: numeral(totalMonthlyBudget).format('$0.00a'), percentageValue: '' }
  ];

  return { mainLegendData, totalSpendPercent: numeral(totalSpend / totalMonthlyBudget).value() };
};

export const mapStateToProps = (state: ReduxStore) => ({
  ..._pick(state, ['adCampaigns', 'adPortfolios', 'adEntities', 'entitySearchService', 'retailer', 'app']),
  mainEntity: state.entityService.mainEntity
});
